import { useMutation } from "@tanstack/react-query";
import { useUserStore } from "../user/user.store";
import { authApi } from "./auth.api";
import { useAuthStore } from "./auth.store";
import type {
	LoginRequest,
	RefreshTokenRequest,
	RegisterRequest,
	ResendVerificationRequest,
	VerifyEmailRequest,
} from "./auth.types";

export const useRegister = () => {
	return useMutation({
		mutationFn: (request: RegisterRequest) => authApi.register(request),
	});
};

export const useLogin = () => {
	const setAuth = useAuthStore((state) => state.setAuth);
	const setMe = useUserStore((state) => state.setMe);

	return useMutation({
		mutationFn: (request: LoginRequest) => authApi.login(request),
		onSuccess: (data) => {
			setAuth(data);
			setMe(data.user);
		},
	});
};

export const useRefreshToken = () => {
	const setAccessToken = useAuthStore((state) => state.setAccessToken);

	return useMutation({
		mutationFn: (request: RefreshTokenRequest) => authApi.refresh(request),
		onSuccess: (data) => setAccessToken(data.accessToken),
	});
};

export const useVerifyEmail = () => {
	const setAuth = useAuthStore((state) => state.setAuth);
	const setMe = useUserStore((state) => state.setMe);

	return useMutation({
		mutationFn: (request: VerifyEmailRequest) => authApi.verifyEmail(request),
		onSuccess: (data) => {
			if (data.accessToken && data.refreshToken && data.user) {
				setAuth({ accessToken: data.accessToken, refreshToken: data.refreshToken, user: data.user });
				setMe(data.user);
			}
		},
	});
};

export const useResendVerification = () => {
	return useMutation({
		mutationFn: (request: ResendVerificationRequest) => authApi.resendVerification(request),
	});
};

export const useLogout = () => {
	const clearAuth = useAuthStore((state) => state.clear);
	const clearUser = useUserStore((state) => state.clear);

	return useMutation({
		mutationFn: () => authApi.logout(),
		onSuccess: () => {
			clearAuth();
			clearUser();
		},
	});
};
